import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { TextField } from '@/components/forms/TextField';
import { TextareaField } from '@/components/forms/TextareaField';
import { FormErrorSummary } from '@/components/forms/FormErrorSummary';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { CoverPhotoUploadField } from '@/components/common/CoverPhotoUploadField';
import { useUploadsInFlight } from '@/hooks/useUploadsInFlight';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { ROUTES } from '@/lib/constants/routes';
import { useEntity } from '@/hooks/useEntity';
import { fetchPartnerProfile } from '@/features/profiles/api/partner-profile.api';
import {
  usePartnerOffers,
  useCreateOffer,
  useSubmitOffer,
  useUpdateOffer,
} from './hooks/usePartnerOffers';
import { OfferPreviewCard } from './components/OfferPreviewCard';
import { offerFormSchema, toApiPayload, toFormValues, type OfferFormValues } from './schema';
import { PARTNER_OFFERS_STRINGS as S } from './partner-offers.constants';

const OFFER_COVER_PHOTO_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'application/pdf'] as const;

/** Mirrors `ProfileHeaderCard.tsx`'s `initialsFor` - a small enough helper not to share. */
function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

const FIELD_LABELS: Record<string, string> = {
  title: S.FIELD_TITLE,
  description: S.FIELD_DESCRIPTION,
  expiresAt: S.FIELD_EXPIRES_AT,
};

const DEFAULT_VALUES: OfferFormValues = {
  title: '',
  subtitle: '',
  coverPhotoFileKey: '',
  description: '',
  availableFrom: '',
  expiresAt: '',
  claimLinkUrl: '',
  claimEmail: '',
  internalNotes: '',
};

/**
 * Create/Edit Offer form (FR-29, MYN-104), registered at
 * `ROUTES.PARTNER_OFFER_NEW` and `ROUTES.PARTNER_OFFER_EDIT`. Field set is
 * the canonical Special Offers/Perks table from data-fields.md (re-supplied
 * directly by the engineer) - see `schema.ts` for why this replaced the
 * ticket's earlier, Figma-only field set (offerType/discount/promo code/
 * redemption link/max redemptions, none of which survive in the canonical
 * table).
 *
 * "Logo (to be pulled from Trusted Partner)" is read-only: it shows the
 * partner's own profile logo (fetched here, not part of the offer's own
 * data), never a second upload control - the offer has no logo of its own.
 *
 * There is no separate "Save as Draft" action: a draft with no path back to
 * Submitted would be dead UI (the console has no submit button, only
 * Withdraw/Publish/Deactivate on already-submitted offers). "Send for
 * Review" therefore chains create-or-update with submit in one user action.
 *
 * No standalone GET-by-id endpoint exists for a single offer (the Backend
 * only lists `/partners/:entityId/offers`), so edit mode finds its record in
 * the same list the console already fetches, exactly like the console does.
 */
export function OfferFormPage() {
  const { offerId } = useParams<{ offerId?: string }>();
  const isEditing = Boolean(offerId);
  const navigate = useNavigate();
  const uploads = useUploadsInFlight();
  const entity = useEntity();
  const entityId = entity?.entityId ?? '';

  const offersQuery = usePartnerOffers(entityId);
  // The cover just picked in this session, for the live preview (the form itself only holds its S3 key).
  const [previewCover, setPreviewCover] = useState<{ url: string; isPdf: boolean } | null>(null);
  const existingOffer = isEditing ? offersQuery.data?.find((o) => o.id === offerId) : undefined;

  const profileQuery = useQuery({
    queryKey: ['profile', 'partner', entityId],
    queryFn: () => fetchPartnerProfile(entityId),
    enabled: Boolean(entityId),
  });

  const createOffer = useCreateOffer(entityId);
  const updateOffer = useUpdateOffer(entityId, offerId ?? '');
  const submitOffer = useSubmitOffer(entityId);
  const [sendError, setSendError] = useState<string | undefined>();

  const form = useForm<OfferFormValues>({
    resolver: zodResolver(offerFormSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const { register, handleSubmit, reset, setValue, formState, control } = form;

  // MYN-106 AC2: the preview updates as the partner types. `useWatch` with an
  // explicit field list rather than `watch()`: the latter re-renders this whole
  // page - including the cover-photo upload control - on every keystroke in
  // any field, and the preview only needs these five.
  const preview = useWatch({
    control,
    name: ['title', 'subtitle', 'description', 'expiresAt', 'claimLinkUrl', 'claimEmail'],
  });
  const [
    previewTitle,
    previewSubtitle,
    previewDescription,
    previewExpiresAt,
    previewClaimLink,
    previewClaimEmail,
  ] = preview;

  useEffect(() => {
    if (existingOffer) reset(toFormValues(existingOffer));
  }, [existingOffer, reset]);

  const isPending = createOffer.isPending || updateOffer.isPending || submitOffer.isPending;

  const onSend = handleSubmit(async (values) => {
    setSendError(undefined);
    const payload = toApiPayload(values);
    try {
      const offer = isEditing
        ? await updateOffer.mutateAsync(payload)
        : await createOffer.mutateAsync(payload);
      await submitOffer.mutateAsync(offer.id);
      toast.success(S.SUBMIT_SUCCESS);
      navigate(ROUTES.PARTNER_OFFERS, { replace: true });
    } catch (error) {
      setSendError(getApiErrorMessage(error, S.SAVE_ERROR_FALLBACK));
    }
  });

  if (isEditing && offersQuery.isPending) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" label={S.EDIT_FORM_TITLE} />
      </div>
    );
  }

  if (isEditing && (offersQuery.isError || (offersQuery.isSuccess && !existingOffer))) {
    return (
      <ErrorState
        title={offersQuery.isError ? S.LOAD_ERROR_TITLE : S.NOT_FOUND_TITLE}
        description={offersQuery.isError ? S.LOAD_ERROR_DESCRIPTION : S.NOT_FOUND_DESCRIPTION}
        onRetry={offersQuery.isError ? () => void offersQuery.refetch() : undefined}
      />
    );
  }

  const partnerLogoUrl = profileQuery.data?.logoUrl;
  const partnerBusinessName = profileQuery.data?.businessName ?? '';

  return (
    <div className="bg-page-bg min-h-full">
      {/* Wider than the form alone needs (was max-w-3xl): the live preview
          sits beside it from `lg` up, and below that the two stack with the
          preview first, so a partner on a narrow screen sees what they are
          building before the fields they build it with. */}
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10">
        <div>
          <Link to={ROUTES.PARTNER_OFFERS} className="text-p6 text-dkgreen font-semibold">
            {S.BACK_TO_PERKS}
          </Link>
          <h1 className="text-h4 text-navy mt-2 font-bold">
            {isEditing ? S.EDIT_FORM_TITLE : S.CREATE_FORM_TITLE}
          </h1>
          <p className="text-p6 text-slate mt-1">
            {isEditing ? S.EDIT_FORM_DESCRIPTION : S.CREATE_FORM_DESCRIPTION}
          </p>
        </div>

        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
          {/* Preview first in the DOM so it leads on a stacked narrow screen,
              then ordered second on `lg` where it sits in the right column. */}
          <div className="lg:sticky lg:top-6 lg:order-2">
            <OfferPreviewCard
              title={previewTitle ?? ''}
              subtitle={previewSubtitle ?? ''}
              description={previewDescription ?? ''}
              expiresAt={previewExpiresAt ?? ''}
              claimLinkUrl={previewClaimLink ?? ''}
              claimEmail={previewClaimEmail ?? ''}
              coverPhotoUrl={previewCover ? (previewCover.isPdf ? null : previewCover.url) : existingOffer?.coverPhotoUrl}
              partnerLogoUrl={partnerLogoUrl}
              partnerName={partnerBusinessName}
              partnerInitials={initialsFor(partnerBusinessName)}
            />
          </div>

          <form onSubmit={onSend} noValidate className="flex flex-col gap-6 lg:order-1">
            {sendError && (
              <p role="alert" className="text-danger text-sm">
                {sendError}
              </p>
            )}

            <section className="rounded-card border-divider shadow-card flex flex-col gap-5 border bg-white p-6">
              <h2 className="text-p5 text-navy font-bold">{S.SECTION_DETAILS}</h2>

              <div className="flex flex-col gap-1.5">
                <FieldLabel label={S.FIELD_LOGO} variant="caps" />
                <div className="flex items-center gap-3">
                  <Avatar
                    photoUrl={partnerLogoUrl ?? undefined}
                    initials={initialsFor(partnerBusinessName)}
                    size={48}
                  />
                  <span className="text-p6 text-slate">{S.FIELD_LOGO_HINT}</span>
                </div>
              </div>

              <TextField
                label={S.FIELD_TITLE}
                required
                maxLength={100}
                error={formState.errors.title?.message}
                {...register('title')}
              />
              <TextField
                label={S.FIELD_SUBTITLE}
                optional
                maxLength={100}
                error={formState.errors.subtitle?.message}
                {...register('subtitle')}
              />
              <CoverPhotoUploadField
                onUploadingChange={uploads.track('cover')}
                label={S.FIELD_COVER_PHOTO}
                hint={S.FIELD_COVER_PHOTO_HINT}
                uploadCaption={S.FIELD_COVER_PHOTO_UPLOAD}
                replaceCaption={S.FIELD_COVER_PHOTO_REPLACE}
                wrongTypeMessage={S.FIELD_COVER_PHOTO_WRONG_TYPE}
                tooLargeMessage={S.FIELD_COVER_PHOTO_TOO_LARGE}
                uploadFailedMessage={S.FIELD_COVER_PHOTO_UPLOAD_FAILED}
                uploadKind="partnerOfferCoverPhoto"
                acceptedTypes={OFFER_COVER_PHOTO_ACCEPTED_TYPES}
                coverPhotoUrl={existingOffer?.coverPhotoUrl}
                onPreviewChange={setPreviewCover}
                onUploaded={(fileKey) =>
                  setValue('coverPhotoFileKey', fileKey, { shouldDirty: true })
                }
              />
              <TextareaField
                label={S.FIELD_DESCRIPTION}
                required
                maxLength={300}
                showCount
                rows={4}
                error={formState.errors.description?.message}
                {...register('description')}
              />
            </section>

            <section className="rounded-card border-divider shadow-card flex flex-col gap-5 border bg-white p-6">
              <h2 className="text-p5 text-navy font-bold">{S.SECTION_VISIBILITY}</h2>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField
                  label={S.FIELD_AVAILABLE_FROM}
                  optional
                  type="date"
                  {...register('availableFrom')}
                />
                <TextField
                  label={S.FIELD_EXPIRES_AT}
                  optional
                  type="date"
                  error={formState.errors.expiresAt?.message}
                  {...register('expiresAt')}
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField
                  label={S.FIELD_CLAIM_LINK}
                  type="url"
                  helpText={S.FIELD_CLAIM_LINK_HINT}
                  error={formState.errors.claimLinkUrl?.message}
                  {...register('claimLinkUrl')}
                />
                <TextField
                  label={S.FIELD_CLAIM_EMAIL}
                  type="email"
                  helpText={S.FIELD_CLAIM_EMAIL_HINT}
                  error={formState.errors.claimEmail?.message}
                  {...register('claimEmail')}
                />
              </div>
            </section>

            <section className="rounded-card border-divider shadow-card flex flex-col gap-5 border bg-white p-6">
              <h2 className="text-p5 text-navy font-bold">{S.SECTION_NOTES}</h2>
              <TextareaField
                label={S.FIELD_INTERNAL_NOTES}
                optional
                rows={3}
                {...register('internalNotes')}
              />
            </section>

            <FormErrorSummary
              errors={formState.errors}
              labels={FIELD_LABELS}
              title={S.VALIDATION_SUMMARY_TITLE}
            />

            <div className="flex items-center justify-end gap-4">
              <button
                type="button"
                className="text-p6 text-red cursor-pointer font-semibold hover:opacity-75"
                onClick={() => navigate(ROUTES.PARTNER_OFFERS)}
              >
                {S.DISCARD}
              </button>
              {/* Blocked mid-transfer - see useUploadsInFlight. */}
              <Button type="submit" variant="green" disabled={isPending || uploads.isUploading}>
                {isPending ? S.SAVING : S.SEND_FOR_REVIEW}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
